import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { MessageSquare, Send, Pin, Trash2, MoreHorizontal, Edit2, Flag } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

interface Comment {
    id: string;
    userId: string;
    userName: string;
    userAvatar?: string;
    content: string;
    timestamp: string;
    isPinned?: boolean;
    isCreatorUpdate?: boolean;
    isEdited?: boolean;
}

interface CommentSectionProps {
    entityId: string;
    currentUserId?: string;
    creatorId?: string;
    onCommentAdded?: (content: string) => void;
    onCommentDeleted?: (commentId: string) => void;
    title?: string;
    placeholder?: string;
}

function getInitialComments(entityId: string): Comment[] {
    if (entityId === '1') {
        return [
            {
                id: 'c1',
                userId: 'u1',
                userName: 'Tennis Club NYC',
                userAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
                content: '🎾 Important Update: Please arrive 15 minutes early for equipment check-in! Looking forward to seeing everyone there.',
                timestamp: '2025-09-28T10:30:00',
                isPinned: true,
                isCreatorUpdate: true,
            },
            {
                id: 'c2',
                userId: 'u2',
                userName: 'Sarah Johnson',
                userAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
                content: 'Really excited for this tournament! What should we bring?',
                timestamp: '2025-09-27T14:20:00',
            },
        ];
    } else if (entityId === '3') {
        return [
            {
                id: 'c5',
                userId: 'u4',
                userName: 'Zen Studios',
                userAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
                content: '🧘 Reminder: Bring your own yoga mat. We have a few extras, but they go quickly!',
                timestamp: '2025-09-26T18:00:00',
                isPinned: true,
                isCreatorUpdate: true,
            },
        ];
    }
    return [];
}

function formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return minutes + 'm ago';
    if (hours < 24) return hours + 'h ago';
    if (days < 7) return days + 'd ago';

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    if (year !== now.getFullYear()) {
        return month + ' ' + day + ', ' + year;
    }
    return month + ' ' + day;
}

export function CommentSection({
    entityId,
    currentUserId = 'u2',
    creatorId = 'u1',
    onCommentAdded,
    onCommentDeleted,
    title = 'Discussion & Updates',
    placeholder = 'Share your thoughts or ask a question...',
}: CommentSectionProps) {
    const [comments, setComments] = useState<Comment[]>(() => getInitialComments(entityId));
    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState('');
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSubmitComment = async () => {
        if (!newComment.trim()) return;
        setIsSubmitting(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        const comment: Comment = {
            id: 'c' + Date.now(),
            userId: currentUserId,
            userName: 'You',
            content: newComment,
            timestamp: new Date().toISOString(),
            isCreatorUpdate: currentUserId === creatorId,
        };

        setComments([...comments, comment]);
        setNewComment('');
        setIsSubmitting(false);
        if (onCommentAdded) onCommentAdded(newComment);
    };

    const handleDeleteComment = (commentId: string) => {
        setComments(comments.filter(c => c.id !== commentId));
        if (onCommentDeleted) onCommentDeleted(commentId);
    };

    const handlePinComment = (commentId: string) => {
        setComments(comments.map(c => c.id === commentId ? { ...c, isPinned: !c.isPinned } : c));
    };

    const handleEditComment = (comment: Comment) => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
    };

    const handleSaveEdit = (commentId: string) => {
        setComments(comments.map(c => c.id === commentId ? { ...c, content: editContent, isEdited: true } : c));
        setEditingCommentId(null);
        setEditContent('');
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditContent('');
    };

    const sortedComments = [...comments].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

    const isCurrentUserCreator = currentUserId === creatorId;

    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-muted-foreground" />
                        <h2>{title}</h2>
                    </div>
                    <Badge variant="secondary">{comments.length}</Badge>
                </div>

                <div className="mb-6">
                    <div className="flex gap-3">
                        <Avatar className="w-9 h-9 flex-shrink-0">
                            <AvatarImage src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" />
                            <AvatarFallback>YO</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-2">
                            <Textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder={placeholder}
                                className="resize-none min-h-[80px]"
                                disabled={isSubmitting}
                            />
                            <div className="flex justify-end">
                                <Button onClick={handleSubmitComment} disabled={!newComment.trim() || isSubmitting} size="sm">
                                    <Send className="w-4 h-4 mr-2" />
                                    {isSubmitting ? 'Posting...' : 'Post Comment'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator className="mb-6" />

                <div className="space-y-4">
                    {sortedComments.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No comments yet</p>
                            <p className="text-sm">Be the first to share your thoughts!</p>
                        </div>
                    ) : (
                        sortedComments.map((comment) => {
                            const isOwnComment = comment.userId === currentUserId;
                            const isEditing = editingCommentId === comment.id;

                            return (
                                <div key={comment.id} className={'relative p-4 rounded-lg transition-colors ' + (comment.isPinned ? 'bg-accent/50 border border-border' : 'bg-muted/30')}>
                                    {comment.isPinned && (
                                        <div className="absolute top-2 right-2">
                                            <Badge variant="secondary" className="text-xs">
                                                <Pin className="w-3 h-3 mr-1" />
                                                Pinned
                                            </Badge>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <Avatar className="w-9 h-9 flex-shrink-0">
                                            <AvatarImage src={comment.userAvatar} />
                                            <AvatarFallback>{comment.userName.slice(0, 2).toUpperCase()}</AvatarFallback>
                                        </Avatar>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="font-medium">{comment.userName}</span>
                                                {comment.isCreatorUpdate && (
                                                    <Badge variant="default" className="text-xs">Organizer</Badge>
                                                )}
                                                <span className="text-sm text-muted-foreground">{formatTimestamp(comment.timestamp)}</span>
                                                {comment.isEdited && (
                                                    <span className="text-xs text-muted-foreground italic">(edited)</span>
                                                )}
                                            </div>

                                            {isEditing ? (
                                                <div className="space-y-2 mt-2">
                                                    <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="resize-none min-h-[60px]" />
                                                    <div className="flex gap-2">
                                                        <Button size="sm" onClick={() => handleSaveEdit(comment.id)}>Save</Button>
                                                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-foreground whitespace-pre-wrap break-words">{comment.content}</p>
                                            )}
                                        </div>

                                        {(isOwnComment || isCurrentUserCreator) && !isEditing && (
                                            <div className="relative" ref={openDropdownId === comment.id ? dropdownRef : null}>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 flex-shrink-0 opacity-70 hover:opacity-100 hover:bg-accent"
                                                    type="button"
                                                    onClick={() => setOpenDropdownId(openDropdownId === comment.id ? null : comment.id)}
                                                >
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                                {openDropdownId === comment.id && (
                                                    <div
                                                        className="absolute top-0 left-full ml-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 py-0.5"
                                                        style={{ zIndex: 99999 }}
                                                    >
                                                        {isOwnComment && (
                                                            <button
                                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center"
                                                                onClick={() => {
                                                                    handleEditComment(comment);
                                                                    setOpenDropdownId(null);
                                                                }}
                                                            >
                                                                <Edit2 className="w-3 h-3 mr-1.5" />
                                                                Edit
                                                            </button>
                                                        )}
                                                        {isCurrentUserCreator && (
                                                            <button
                                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center"
                                                                onClick={() => {
                                                                    handlePinComment(comment.id);
                                                                    setOpenDropdownId(null);
                                                                }}
                                                            >
                                                                <Pin className="w-3 h-3 mr-1.5" />
                                                                {comment.isPinned ? 'Unpin' : 'Pin'}
                                                            </button>
                                                        )}
                                                        {isOwnComment && (
                                                            <button
                                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-600 flex items-center"
                                                                onClick={() => {
                                                                    handleDeleteComment(comment.id);
                                                                    setOpenDropdownId(null);
                                                                }}
                                                            >
                                                                <Trash2 className="w-3 h-3 mr-1.5" />
                                                                Delete
                                                            </button>
                                                        )}
                                                        {!isOwnComment && (
                                                            <button
                                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center"
                                                                onClick={() => setOpenDropdownId(null)}
                                                            >
                                                                <Flag className="w-3 h-3 mr-1.5" />
                                                                Report
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </CardContent>
        </Card>
    );
}